/**
 * Shared lottery bet-amount limits & validation.
 *
 * Lottery providers only accept bets that are multiples of 10 (50, 60, 70...).
 * That step is a fixed global rule. The per-game minimum / maximum come from the
 * API game object (LotteryGame.min_bet / max_bet), which are stored in cents.
 * When a game has no configured minimum we fall back to MIN_BET_AMOUNT (the
 * platform-wide default).
 */
import {MIN_BET_AMOUNT} from "../components/ticketsboard/ticketsboard.consts";

// Fixed provider rule: every bet amount must be a multiple of this.
export const LOTTERY_BET_STEP = 10;

/**
 * Reads the per-game min/max bet limits from an API game object.
 * min_bet / max_bet are stored in cents; a missing min falls back to the
 * platform default, a missing max means "no maximum".
 * @param {Object} apiGame - raw game object from the lottery games API
 * @returns {{minBet: number, maxBet: number, step: number}} limits in Bs.
 */
export const getGameBetLimits = apiGame => ({
  minBet: apiGame?.min_bet ? apiGame.min_bet / 100 : MIN_BET_AMOUNT,
  maxBet: apiGame?.max_bet ? apiGame.max_bet / 100 : 0,
  step: LOTTERY_BET_STEP,
});

/**
 * Validates a bet amount against the limits.
 * @param {number|string} amount - amount in Bs.
 * @param {{minBet?: number, maxBet?: number, step?: number}} limits
 * @returns {{valid: boolean, reason: ("empty"|"min"|"max"|"step"|null), minBet: number, maxBet: number, step: number}}
 */
export const validateBetAmount = (amount, {minBet = 0, maxBet = 0, step = LOTTERY_BET_STEP} = {}) => {
  const base = {minBet, maxBet, step};
  if (amount === null || amount === undefined || amount === "" || Number.isNaN(Number(amount))) {
    return {valid: false, reason: "empty", ...base};
  }
  const n = Number(amount);
  if (n <= 0) return {valid: false, reason: "empty", ...base};
  if (minBet > 0 && n < minBet) return {valid: false, reason: "min", ...base};
  if (maxBet > 0 && n > maxBet) return {valid: false, reason: "max", ...base};
  if (step > 0 && Math.round(n) % step !== 0) return {valid: false, reason: "step", ...base};
  return {valid: true, reason: null, ...base};
};

/** Convenience boolean wrapper around {@link validateBetAmount}. */
export const isBetAmountValid = (amount, limits) => validateBetAmount(amount, limits).valid;

/**
 * Maps a validation result to a translated, user-facing error message.
 * Returns null when the amount is valid or simply empty.
 * @param {Object} result - output of {@link validateBetAmount}
 * @param {Function} t - i18next translate function
 * @returns {string|null}
 */
export const betAmountMessage = (result, t) => {
  if (!result || result.valid) return null;
  switch (result.reason) {
    case "min":
      return t("lotteryErrorMinBetAmount", {min: result.minBet});
    case "max":
      return t("lotteryErrorMaxBetAmount", {max: result.maxBet});
    case "step":
      return t("lotteryErrorBetStep", {step: result.step});
    default:
      return null;
  }
};

/**
 * Builds a short, always-visible hint describing the active limits, e.g.
 * "Mínimo 50 Bs · múltiplos de 10 · máximo 1000 Bs".
 * @param {{minBet?: number, maxBet?: number, step?: number}} limits
 * @param {Function} t - i18next translate function
 * @returns {string}
 */
export const betLimitsHint = ({minBet = 0, maxBet = 0, step = LOTTERY_BET_STEP} = {}, t) => {
  const parts = [];
  if (minBet > 0) parts.push(t("lotteryBetHintMin", {min: minBet}));
  parts.push(t("lotteryBetHintStep", {step}));
  if (maxBet > 0) parts.push(t("lotteryBetHintMax", {max: maxBet}));
  return parts.join(" · ");
};
